import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Typography,
  Container
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';
import NotFound from '../components/NotFound';

interface SubmissionData {
  id: string;
  uniqueId: string;
  formTitle: string;
  createdAt: string;
  status: string;
  paymentStatus?: string;
  paymentAmount?: number;
}

const UserPdfView: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionData, setSubmissionData] = useState<SubmissionData | null>(null);

  useEffect(() => {
    const fetchSubmissionData = async () => {
      if (!uniqueId) {
        setError('No submission ID provided');
        setLoading(false);
        return;
      }

      // remove dashes if any
      const cleanedUniqueId = uniqueId.replace(/-/g, '');


      try {
        // First, fetch basic submission info to display
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/public/${cleanedUniqueId}`
        );
        
        setSubmissionData(response.data);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching submission:', err);
        setError(err.response?.data?.message || 'Failed to load submission');
        setLoading(false);
      }
    };

    fetchSubmissionData();
  }, [uniqueId]);

  const handleDownloadPdf = async () => {
    if (!uniqueId) return;

    try {
      setPdfLoading(true);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/pdf/${uniqueId}`,
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${uniqueId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setError(err.response?.data?.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleViewPdf = async () => {
    if (!uniqueId) return;

    try {
      setPdfLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/pdf/${uniqueId}`,
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Open in new tab
      window.open(url, '_blank');
      
    } catch (err: any) {
      console.error('Error viewing PDF:', err);
      setError(err.response?.data?.message || 'Failed to view PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!uniqueId) return;

    try {
      setPdfLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/pdf/${uniqueId}`,
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
      
    } catch (err: any) {
      console.error('Error printing PDF:', err);
      setError(err.response?.data?.message || 'Failed to print PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <NotFound
        title="Error"
        message={error}
      />
    );
  }

  if (!submissionData) {
    return (
      <Container maxWidth="md">
        <Box mt={4}>
          <Alert severity="warning">No submission data found</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={4}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom color="primary">
              Submission PDF
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              {submissionData.formTitle}
            </Typography>
            
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Submission ID:</strong> {submissionData.uniqueId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Date:</strong> {new Date(submissionData.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Status:</strong> {submissionData.status}
              </Typography>
              {submissionData.paymentStatus && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Payment Status:</strong> {submissionData.paymentStatus}
                  </Typography>
                  {submissionData.paymentAmount && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Payment Amount:</strong> ৳{submissionData.paymentAmount}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {submissionData.status !== 'completed' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                PDF download is only available when the submission status is "completed". Current status: {submissionData.status}
              </Alert>
            )}

            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPdf}
                disabled={loading || pdfLoading || submissionData.status !== 'completed'}
              >
                {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ViewIcon />}
                onClick={handleViewPdf}
                disabled={loading || pdfLoading || submissionData.status !== 'completed'}
              >
                {pdfLoading ? 'Generating PDF...' : 'View PDF'}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={loading || pdfLoading || submissionData.status !== 'completed'}
              >
                {pdfLoading ? 'Generating PDF...' : 'Print'}
              </Button>
            </Box>

            {pdfLoading && (
              <Box mt={2} display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="primary">
                  Please wait while we generate your PDF. This may take up to 60 seconds...
                </Typography>
              </Box>
            )}

            <Box mt={3}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ⏱️ PDF Generation Notice
                </Typography>
                <Typography variant="body2">
                  PDF generation may take 10-60 seconds depending on form complexity. Please wait patiently after clicking any PDF button and do not refresh the page.
                </Typography>
              </Alert>
              
              <Alert severity="info">
                This PDF contains your submitted form data. You can download, view, or print it for your records.
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default UserPdfView;
